import { forwardRef } from 'react'

import { createPageComponent } from './create-page-component'
import { createUseShouldRender } from './create-use-should-render'
import { PageContract, RegisterPageOptions } from './types'

/**
 * Returns a page contract with the given options.
 *
 * The page contract delivers the page suffix and page component which automatically determines if the page should be rendered or not.
 *
 * Page component will not be rendered until `shouldRender` function is resolved. If `shouldRender` function is not provided, then underlying page component will always be rendered.
 *
 * `PageComponent` returned by page contract lazy loads underlying page component only if it should be rendered. This enables a simplified and short syntax when rendering the page in your app.
 * @example
 * ```tsx
 * export function App() {
 *   return <SomePageContract.PageComponent /> // only renders if the page should render
 * }
 * ```
 *
 * For multi-contract page rendering in an app, see {@link RenderFirstPageContract}.
 *
 * When creating a page contract, you must provide both the should-render logic and page component as import factories
 * to not impact the bundle size of your app.
 *
 * @example
 * `pages/<Page>/index.ts`
 * ```ts
 * export const PageContract = registerPage({
 *   // ...remain options
 *   pageFactory: () => import('./<Page>'),
 *   shouldRenderFactory: () => import('./helpers/should-render'),
 * })
 *
 * ```
 *
 * @param options
 * @param options.suffix - The suffix of the page, see {@link PageSuffix}.
 * @param options.pageFactory - A function that returns a promise that resolves to the page component
 * @param options.shouldRenderFactory - A function that returns a promise that resolves to the module containing `shouldRender` function.
 * @param options.analyticsConfig - The page analytics config which is used as baseline for building unrecoverable error events. See {@link UnrecoverableEventConfig}.
 */
export function registerPage<T>(
  options: RegisterPageOptions<T>,
): PageContract<T> {
  const useShouldRender = createUseShouldRender<T>(options)

  return {
    suffix: options.suffix,
    PageComponent: forwardRef(createPageComponent<T>(useShouldRender, options)),
  } as PageContract<T>
}
